import { useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, Search } from 'lucide-react';

import type {
  AdminOrganization,
  OrganizationLicenseStatus,
  OrganizationPlanKey,
  OrganizationType,
} from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { Badge, Button, EmptyState, ErrorState, Select, TableSkeleton, TextInput } from '@/components/ui';
import { billingDate, count, money } from '@/lib/format';
import { useDebounced, useResource } from '@/lib/hooks';
import { usePageSize } from '@/lib/pageSize';
import { useDataScope } from '@/lib/dataPurpose';
import { PurposeBadge } from '@/components/DataPurpose';

const STATUS_TONE: Record<OrganizationLicenseStatus, 'neutral' | 'positive' | 'caution' | 'critical' | 'info'> = {
  draft: 'neutral',
  pending: 'info',
  active: 'positive',
  grace: 'caution',
  readonly: 'critical',
  canceled: 'neutral',
};

const LICENSE_STATUSES: OrganizationLicenseStatus[] = [
  'draft', 'pending', 'active', 'grace', 'readonly', 'canceled',
];

const ORGANIZATION_TYPES: OrganizationType[] = ['commercial', 'club', 'nlz', 'federation'];

interface AdminOrganizationPage {
  items: AdminOrganization[];
  total: number;
}

export function LicensesPage() {
  const { t, i18n } = useTranslation();
  const { scope } = useDataScope();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsRef = useRef(searchParams);
  const locationKeyRef = useRef(location.key);
  if (locationKeyRef.current !== location.key) {
    paramsRef.current = searchParams;
    locationKeyRef.current = location.key;
  }
  const search = searchParams.get('search')?.slice(0, 100) ?? '';
  const typeValue = searchParams.get('organization_type');
  const organizationType = ORGANIZATION_TYPES.find((value) => value === typeValue) ??
    (typeValue === 'all' ? 'all' : 'commercial');
  const statusValue = searchParams.get('status');
  const status = LICENSE_STATUSES.find((value) => value === statusValue) ?? '';
  const pageValue = Number(searchParams.get('page'));
  const page = Number.isSafeInteger(pageValue) && pageValue > 0 ? pageValue : 1;
  const [pageSize, setPageSize] = usePageSize('licenses');
  const debouncedSearch = useDebounced(search);
  const language = i18n.language;
  const returnTo = `${location.pathname}${location.search}`;
  const planLabel = (planKey: OrganizationPlanKey) =>
    planKey === 'unlimited'
      ? t('licenses.unlimited')
      : t('licenses.playerPlan', { count: Number(planKey) });

  function updateFilters(changes: Record<string, string>, resetPage = true) {
    const next = new URLSearchParams(paramsRef.current);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (resetPage) next.delete('page');
    paramsRef.current = next;
    setSearchParams(next, { replace: true });
  }

  const { data, error, loading, reload } = useResource<AdminOrganizationPage>(
    '/api/v1/admin/organizations',
    {
      q: debouncedSearch,
      status: status || undefined,
      organization_type: organizationType === 'all' ? undefined : organizationType,
      offset: (page - 1) * pageSize,
      limit: pageSize,
      data_purpose: scope,
    },
    [debouncedSearch, status, organizationType, page, pageSize, scope],
  );

  useEffect(() => {
    if (search !== debouncedSearch || loading || error || !data) return;
    const lastPage = Math.max(1, Math.ceil(data.total / pageSize));
    if (page > lastPage) updateFilters({ page: lastPage === 1 ? '' : String(lastPage) }, false);
    // Keep the URL correction aligned with the latest filter action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, debouncedSearch, error, loading, page, pageSize, search]);

  const columns: Column<AdminOrganization>[] = [
    {
      key: 'organization',
      header: t('licenses.organization'),
      render: (row) => (
        <div>
        <Link
          to={`/clubs/${encodeURIComponent(row.id)}?${new URLSearchParams({ from: returnTo })}`}
          className="break-words font-medium text-ink hover:text-brand hover:underline"
        >
          {row.name}
        </Link>
        <div className="mt-1"><PurposeBadge purpose={row.data_purpose} /></div>
        </div>
      ),
    },
    {
      key: 'type',
      header: t('licenses.typeColumn'),
      render: (row) => <span className="break-words text-ink-muted">{t(`clubs.organizationTypes.${row.organization_type}`)}</span>,
    },
    {
      key: 'status',
      header: t('licenses.status'),
      render: (row) => row.license ? (
        <div>
          <Badge tone={STATUS_TONE[row.license.status]}>
            {t(`licenses.statuses.${row.license.status}`)}
          </Badge>
          {row.license.status === 'grace' && row.license.grace_until ? (
            <div className="mt-1 text-2xs text-ink-subtle">
              {t('licenses.graceUntil', { date: billingDate(row.license.grace_until, language) })}
            </div>
          ) : null}
          {row.license.billing_flags?.active_dispute ? (
            <div className="mt-1 text-2xs text-ink-muted">{t('licenses.disputeOpen')}</div>
          ) : null}
          {row.license.billing_flags?.access_revoked ? (
            <div className="mt-1 text-2xs text-ink-muted">
              {t(row.license.billing_flags.revocation_reason === 'fully_refunded'
                ? 'licenses.accessEndedRefund' : 'licenses.accessEndedDispute')}
            </div>
          ) : null}
          {row.license.billing_flags?.contract_ended_for_adjustment && !row.license.billing_flags.access_revoked ? (
            <div className="mt-1 text-2xs text-ink-muted">{t('licenses.paidAccessNoRenewal')}</div>
          ) : null}
          {row.license.billing_flags?.payment_review_required ? (
            <div className="mt-1 text-2xs text-ink-muted">{t('licenses.paymentReviewRequired')}</div>
          ) : null}
        </div>
      ) : (
        <Badge>{t('licenses.free')}</Badge>
      ),
    },
    {
      key: 'plan',
      header: t('licenses.plan'),
      render: (row) => row.license ? (
        <div>
          <span className="text-ink-muted">
            {row.license.plan_key
              ? planLabel(row.license.plan_key)
              : t(row.license.pending_plan_key ? 'licenses.notActivated' : 'licenses.noPlanSelected')}
          </span>
          {row.license.pending_plan_key ? (
            <div className="text-2xs text-ink-subtle">
              {t('licenses.checkoutPending', { plan: planLabel(row.license.pending_plan_key) })}
            </div>
          ) : null}
        </div>
      ) : <span aria-hidden className="text-ink-subtle">—</span>,
    },
    {
      key: 'seats',
      header: t('licenses.seats'),
      align: 'right',
      render: (row) => row.license ? (
        <span className="text-ink-muted">
          {count(row.license.occupied_seats, language)} /{' '}
          {row.license.plan_key === 'unlimited'
            ? t('licenses.unlimited')
            : row.license.plan_key && row.license.seat_limit !== null
              ? count(row.license.seat_limit, language)
              : t('licenses.notActivated')}
        </span>
      ) : <span aria-hidden className="text-ink-subtle">—</span>,
    },
    {
      key: 'renewal',
      header: t('licenses.renewal'),
      render: (row) => row.data_purpose === 'production' && row.license?.current_period_end ? (
        <div className="text-ink-muted">
          {row.license.billing_flags?.access_revoked
            ? t('licenses.originalTermUntil', { date: billingDate(row.license.current_period_end, language) })
            : billingDate(row.license.current_period_end, language)}
          {row.license.billing_flags?.access_revoked ? (
            <div className="text-2xs text-ink-subtle">{t('licenses.contractEnded')}</div>
          ) : row.license.billing_flags?.contract_ended_for_adjustment ? (
            <div className="text-2xs text-ink-subtle">{t('licenses.paidAccessNoRenewal')}</div>
          ) : row.license.cancel_at_period_end || row.license.status === 'canceled' ? (
            <div className="text-2xs text-ink-subtle">{t('licenses.endsThen')}</div>
          ) : null}
        </div>
      ) : <span aria-hidden className="text-ink-subtle">—</span>,
    },
    {
      key: 'revenue',
      header: t('licenses.netCustomerFunds'),
      align: 'right',
      render: (row) => row.data_purpose !== 'production' ? <span className="text-ink-subtle">—</span> : row.receipts.length ? (
        <div className="space-y-1">
          {row.receipts.map((receipt) => (
            <div key={receipt.currency}>
              <div className="font-medium text-ink">{money(receipt.net_cents, receipt.currency, language)}</div>
              <div className="text-2xs text-ink-subtle">
                {t('licenses.paid')} {money(receipt.paid_cents, receipt.currency, language)} ·{' '}
                {t('licenses.refunded')} {money(receipt.refunded_cents, receipt.currency, language)}
              </div>
              {receipt.refund_pending_cents > 0 && <div className="text-2xs text-ink-subtle">
                {t('licenses.refundPending')} {money(receipt.refund_pending_cents, receipt.currency, language)}
              </div>}
              {receipt.dispute_withdrawn_cents > 0 && <div className="text-2xs text-ink-subtle">
                {t('licenses.disputeWithdrawn')} {money(receipt.dispute_withdrawn_cents, receipt.currency, language)}
              </div>}
              {receipt.dispute_reinstated_cents > 0 && <div className="text-2xs text-ink-subtle">
                {t('licenses.disputeReinstated')} {money(receipt.dispute_reinstated_cents, receipt.currency, language)}
              </div>}
              {receipt.dispute_fee_cents !== 0 && <div className="text-2xs text-ink-subtle">
                {t('licenses.disputeFees')} {money(receipt.dispute_fee_cents, receipt.currency, language)}
              </div>}
              {receipt.provider_fee_cents - receipt.dispute_fee_cents !== 0 && <div className="text-2xs text-ink-subtle">
                {t('licenses.providerFees')} {money(receipt.provider_fee_cents - receipt.dispute_fee_cents, receipt.currency, language)}
              </div>}
              {receipt.provider_fee_cents !== 0 && <div className="text-2xs text-ink-subtle">
                {t('licenses.providerBalance')} {money(receipt.balance_net_cents, receipt.currency, language)}
              </div>}
            </div>
          ))}
        </div>
      ) : row.license ? (
        <span className="text-ink-muted">{money(0, row.license.currency, language)}</span>
      ) : <span aria-hidden className="text-ink-subtle">—</span>,
    },
  ];

  const filtered = search.trim() !== '' || status !== '' || organizationType !== 'commercial';

  return (
    <>
      <PageHeader title={t('licenses.title')} subtitle={t('licenses.subtitle')} />
      <div className="card">
        <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <TextInput
              type="search"
              maxLength={100}
              aria-label={t('licenses.search')}
              placeholder={t('licenses.search')}
              className="pl-9"
              value={search}
              onChange={(event) => updateFilters({ search: event.target.value })}
            />
          </div>
          <Select
            aria-label={t('licenses.organizationType')}
            value={organizationType}
            onChange={(event) => updateFilters({ organization_type: event.target.value === 'commercial' ? '' : event.target.value })}
          >
            <option value="commercial">{t('clubs.organizationTypes.commercial')}</option>
            <option value="all">{t('licenses.allTypes')}</option>
            <option value="club">{t('clubs.organizationTypes.club')}</option>
            <option value="nlz">{t('clubs.organizationTypes.nlz')}</option>
            <option value="federation">{t('clubs.organizationTypes.federation')}</option>
          </Select>
          <Select
            aria-label={t('licenses.status')}
            value={status}
            onChange={(event) => updateFilters({ status: event.target.value })}
          >
            <option value="">{t('licenses.allStatuses')}</option>
            {LICENSE_STATUSES.map((value) => (
              <option key={value} value={value}>{t(`licenses.statuses.${value}`)}</option>
            ))}
          </Select>
          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              updateFilters({ page: '' }, false);
            }}
          />
        </div>
        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : search !== debouncedSearch || (loading && !data) ? (
          <TableSkeleton columns={7} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<KeyRound className="h-8 w-8" />}
            title={t(filtered ? 'licenses.noMatches' : 'licenses.emptyTitle')}
            body={t(filtered ? 'licenses.adjustFilters' : 'licenses.emptyBody')}
            action={filtered ? <Button onClick={() => updateFilters({ search: '', status: '', organization_type: '' })}>{t('common.reset')}</Button> : undefined}
          />
        ) : (
          <>
            <div className="licenses-table">
              <DataTable caption={t('licenses.title')} columns={columns} rows={data?.items ?? []} rowKey={(row) => row.id} />
            </div>
            <Pagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={(next) => updateFilters({ page: next > 1 ? String(next) : '' }, false)} />
          </>
        )}
      </div>
      <p className="mt-3 text-xs text-ink-subtle">{t('licenses.billingTimeZone')}</p>
      <p className="mt-1 text-xs text-ink-subtle">{t('licenses.explainer')}</p>
    </>
  );
}
